/* eslint-disable no-restricted-syntax */
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import shortid from 'shortid';
import { STATUS_COLORS } from 'constants/statuses';
import BoxIcon from 'images/box-small.svg';
import TimelineDoneIcon from 'images/timeline-done.svg';
import TimelineErrorIcon from 'images/timeline-error.svg';
import TimelineWaitingIcon from 'images/timeline-waiting.svg';
import AlertCircleSvg from 'images/v5/icons/alert-circle.svg';
import CalendarIcon from 'images/v5/icons/calendar.svg';
import PlanSvg from 'images/v5/icons/file-check-01.svg';
import Button from 'layout/atoms/Button/Button';
import CalendarRange from 'layout/atoms/CalendarRange/CalendarRange';
import Dropdown from 'layout/atoms/Dropdown/Dropdown';
import InfoBox from 'layout/atoms/InfoBox/InfoBox';
import InputLabel from 'layout/atoms/InputLabel';
import InputNumber from 'layout/atoms/InputNumber';
import Label from 'layout/atoms/Label';
import Modal from 'layout/atoms/Modal/Modal';
import ParameterDetails from 'layout/atoms/ParameterDetails/ParameterDetails';
import Switch from 'layout/atoms/Switch/Switch';
import Tooltip from 'layout/atoms/Tooltip';
import Grid from 'layout/molecules/Grid/Grid';
import { useAppDispatch } from 'store/index';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { saveOrderComponentVariantDeliveries, selectOrderDetailsItem, selectOrderDetailsStatus, selectOrderDetailsSuppliersRaw, } from 'store/orderDetailsSlice';
const roundToScale = (value, scale = 2) => Number(value.toFixed(scale));
const formatQuantity = (value, scale = 2) => roundToScale(value, scale).toFixed(scale);
function DeliveryPlanner() {
    var _a;
    const { t } = useTranslation(['orders', 'common']);
    const dispatch = useAppDispatch();
    const orderDaetials = useSelector(selectOrderDetailsItem);
    const modalData = (_a = useSelector(selectModalByName('deliveryPlanner'))) === null || _a === void 0 ? void 0 : _a.data;
    const componentVariantId = modalData === null || modalData === void 0 ? void 0 : modalData.componentVariantId;
    const orderSuppliersRaw = useSelector(selectOrderDetailsSuppliersRaw);
    const status = useSelector(selectOrderDetailsStatus);
    const saving = status.saveDeliveries === 'loading';
    const componentVariant = useMemo(() => {
        if (!componentVariantId || !orderSuppliersRaw) {
            return null;
        }
        for (const supplier of orderSuppliersRaw) {
            if (supplier.componentVariants) {
                const found = supplier.componentVariants.find((v) => v.id === componentVariantId);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }, [componentVariantId, orderSuppliersRaw]);
    const calculatedConsumption = useMemo(() => roundToScale((componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.calculatedConsumption) || 0, 2), [componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.calculatedConsumption]);
    const componentName = (componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.name) || '-';
    const productionEndDate = (componentVariant === null || componentVariant === void 0 ? void 0 : componentVariant.productionEndDate)
        ? moment(componentVariant.productionEndDate)
        : null;
    const createEmptyDelivery = useCallback(() => {
        return {
            id: shortid(),
            backendId: undefined,
            type: 'PLANNED',
            quantity: '',
            date: null,
        };
    }, []);
    const mapDeliveriesFromVariant = useCallback((deliveriesSource) => {
        if (!deliveriesSource)
            return [];
        return deliveriesSource
            .filter((delivery) => delivery.deliveryType === 'PLANNED' ||
            delivery.deliveryType === 'PENDING_APPROVAL' ||
            delivery.deliveryType === 'COLLECTED')
            .sort((a, b) => (a.deliveryOrdinal || 0) - (b.deliveryOrdinal || 0))
            .map((delivery) => {
            const isCollected = delivery.deliveryType === 'COLLECTED';
            const quantity = isCollected
                ? delivery.quantityCollected || 0
                : delivery.quantityPlanned || 0;
            const dateRaw = isCollected ? delivery.collectedDate : delivery.plannedDate;
            const type = delivery.deliveryType;
            return {
                id: delivery.id ? String(delivery.id) : shortid(),
                backendId: delivery.id,
                type,
                quantity: String(quantity || 0),
                date: dateRaw ? moment(dateRaw) : null,
                status: delivery.status,
            };
        });
    }, []);
    const [form, setForm] = useState({
        ordered: calculatedConsumption,
        deliveries: [createEmptyDelivery()],
    });
    // Update form when component variant loads
    useEffect(() => {
        if (!componentVariant)
            return;
        const deliveries = mapDeliveriesFromVariant(componentVariant.deliveries);
        setForm({
            ordered: calculatedConsumption,
            deliveries: deliveries.length > 0 ? deliveries : [createEmptyDelivery()],
        });
    }, [componentVariant, calculatedConsumption, mapDeliveriesFromVariant, createEmptyDelivery]);
    const totalCollected = useMemo(() => {
        return form.deliveries.reduce((sum, delivery) => {
            if (delivery.type !== 'COLLECTED')
                return sum;
            return sum + (parseFloat(delivery.quantity) || 0);
        }, 0);
    }, [form.deliveries]);
    const totalPlanned = useMemo(() => {
        return form.deliveries.reduce((sum, delivery) => {
            if (delivery.type !== 'PLANNED' && delivery.type !== 'PENDING_APPROVAL')
                return sum;
            return sum + (parseFloat(delivery.quantity) || 0);
        }, 0);
    }, [form.deliveries]);
    const totalAllocated = useMemo(() => totalCollected + totalPlanned, [totalCollected, totalPlanned]);
    const sortedDeliveries = useMemo(() => {
        return [...form.deliveries].sort((a, b) => {
            // Items without dates go to the end
            if (!a.date && !b.date)
                return 0;
            if (!a.date)
                return 1;
            if (!b.date)
                return -1;
            return a.date.valueOf() - b.date.valueOf();
        });
    }, [form.deliveries]);
    const getDeliveryStatusDisplay = (delivery) => {
        const isCollected = delivery.type === 'COLLECTED';
        const isPendingApproval = delivery.type === 'PENDING_APPROVAL';
        const today = moment();
        // Past its date, or already flagged delayed by the backend (e.g. a same-day rejection)
        const isDelayed = !isCollected &&
            !isPendingApproval &&
            ((delivery.date && delivery.date.isBefore(today, 'day')) || delivery.status === 'ERROR');
        if (isCollected) {
            return {
                label: t('delivered'),
                color: STATUS_COLORS.done,
                icon: React.createElement(TimelineDoneIcon, null),
            };
        }
        if (isPendingApproval) {
            return {
                label: t('delivery_status_awaiting_approval'),
                color: STATUS_COLORS['awaiting-approval'],
                // alert-circle is a general-purpose 24px icon whose stroke is currentColor,
                // so it is sized down and given a white colour to match the other status badges
                icon: React.createElement(AlertCircleSvg, { width: 14, height: 14, style: { color: '#ffffff' } }),
            };
        }
        if (isDelayed) {
            return {
                label: t('delayed'),
                color: STATUS_COLORS.error,
                icon: React.createElement(TimelineErrorIcon, null),
            };
        }
        return {
            label: t('planned_delivery'),
            color: STATUS_COLORS['in-progress'],
            icon: React.createElement(TimelineWaitingIcon, null),
        };
    };
    const remainingToPlan = useMemo(() => {
        const remainder = form.ordered - totalCollected;
        return remainder > 0 ? remainder : 0;
    }, [form.ordered, totalCollected]);
    const allEmpty = useMemo(() => {
        return form.deliveries.every((delivery) => (!delivery.quantity || parseFloat(delivery.quantity) === 0) && delivery.date === null);
    }, [form.deliveries]);
    const isValid = useMemo(() => {
        if (allEmpty) {
            return true;
        }
        const tolerance = 0.0001;
        const allFieldsFilled = form.deliveries.every((delivery) => {
            const quantity = parseFloat(delivery.quantity);
            if (!delivery.date && quantity > 0) {
                return false;
            }
            if (delivery.type === 'COLLECTED' && quantity <= 0) {
                return false;
            }
            if ((delivery.type === 'PLANNED' || delivery.type === 'PENDING_APPROVAL') && quantity <= 0) {
                return false;
            }
            return delivery.date !== null;
        });
        if (!allFieldsFilled) {
            return false;
        }
        if (totalPlanned > remainingToPlan + tolerance) {
            return false;
        }
        if (totalAllocated < form.ordered - tolerance) {
            return false;
        }
        return true;
    }, [allEmpty, form.deliveries, form.ordered, totalAllocated, totalPlanned, remainingToPlan]);
    const { errorMessage, infoMessage } = useMemo(() => {
        if (allEmpty) {
            return { errorMessage: '', infoMessage: '' };
        }
        const tolerance = 0.0001;
        if (totalAllocated < form.ordered - tolerance) {
            const unallocated = form.ordered - totalAllocated;
            return {
                errorMessage: `${t('planned_delivered_cannot_be_less_than_ordered')} ${t('unallocated')}: ${formatQuantity(unallocated)}`,
                infoMessage: '',
            };
        }
        if (totalPlanned > remainingToPlan + tolerance) {
            const exceeded = totalPlanned - remainingToPlan;
            return {
                errorMessage: `${t('cannot_plan_more_than_ordered')} ${t('exceeded_by')}: ${formatQuantity(exceeded)}`,
                infoMessage: '',
            };
        }
        if (totalCollected > form.ordered + tolerance) {
            const excess = totalCollected - form.ordered;
            return {
                errorMessage: '',
                infoMessage: `${t('collection_exceeds_ordered')} ${t('excess')}: ${formatQuantity(excess)}`,
            };
        }
        return { errorMessage: '', infoMessage: '' };
    }, [allEmpty, form.ordered, t, totalAllocated, totalCollected, totalPlanned, remainingToPlan]);
    const buildPayload = (deliveries) => deliveries
        .filter((delivery) => {
        const quantity = parseFloat(delivery.quantity);
        return quantity > 0 && delivery.date !== null;
    })
        .map((delivery) => {
        var _a;
        return ({
            id: delivery.backendId,
            deliveryType: delivery.type,
            quantity: parseFloat(delivery.quantity),
            date: ((_a = delivery.date) === null || _a === void 0 ? void 0 : _a.toISOString()) || moment().toISOString(),
        });
    });
    const persistDeliveries = (deliveries, options = {}) => {
        if (!componentVariantId)
            return Promise.resolve();
        const deliveriesPayload = buildPayload(deliveries);
        return dispatch(saveOrderComponentVariantDeliveries({
            orderComponentVariantId: componentVariantId,
            deliveries: deliveriesPayload,
        }))
            .unwrap()
            .then((variant) => {
            const updatedDeliveries = mapDeliveriesFromVariant(variant === null || variant === void 0 ? void 0 : variant.deliveries);
            const normalizedDeliveries = updatedDeliveries.length > 0 ? updatedDeliveries : [createEmptyDelivery()];
            setForm({
                ordered: calculatedConsumption,
                deliveries: normalizedDeliveries,
            });
            if (options.closeOnSuccess) {
                dispatch(closeModal({ name: 'deliveryPlanner' }));
            }
        });
    };
    const addNewDelivery = () => {
        setForm(Object.assign(Object.assign({}, form), { deliveries: [...form.deliveries, createEmptyDelivery()] }));
    };
    const handleClearAll = () => {
        setForm({
            ordered: form.ordered,
            deliveries: [createEmptyDelivery()],
        });
    };
    const updateDelivery = (id, updater) => {
        setForm((prev) => (Object.assign(Object.assign({}, prev), { deliveries: prev.deliveries.map((delivery) => delivery.id === id ? Object.assign(Object.assign({}, updater(delivery)), { status: undefined }) : delivery) })));
    };
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        updateDelivery(name, (delivery) => (Object.assign(Object.assign({}, delivery), { quantity: value })));
    };
    const handleDateChange = (id, closeDropdown) => (startDate) => {
        updateDelivery(id, (delivery) => (Object.assign(Object.assign({}, delivery), { date: startDate })));
        if (closeDropdown) {
            closeDropdown();
        }
    };
    const handleDeleteDelivery = (id) => {
        const deliveryToDelete = form.deliveries.find((delivery) => delivery.id === id);
        if (!deliveryToDelete)
            return;
        const updatedDeliveries = form.deliveries.filter((delivery) => delivery.id !== id);
        const nextDeliveries = updatedDeliveries.length > 0 ? updatedDeliveries : [createEmptyDelivery()];
        setForm({
            ordered: form.ordered,
            deliveries: nextDeliveries,
        });
        if (deliveryToDelete.backendId) {
            persistDeliveries(nextDeliveries).catch(() => { });
        }
    };
    const handleToggleDeliveryState = (delivery) => {
        const isCollected = delivery.type === 'COLLECTED';
        const nextType = isCollected ? 'PLANNED' : 'COLLECTED';
        if (!isCollected) {
            const quantityValue = parseFloat(delivery.quantity);
            if (!quantityValue || quantityValue <= 0) {
                return;
            }
        }
        const nextDate = !isCollected && !delivery.date ? moment() : delivery.date;
        const updatedDeliveries = form.deliveries.map((item) => item.id === delivery.id
            ? Object.assign(Object.assign({}, item), { type: nextType, date: nextDate, status: undefined }) : item);
        setForm({
            ordered: form.ordered,
            deliveries: updatedDeliveries,
        });
        persistDeliveries(updatedDeliveries).catch(() => { });
    };
    const handleSubmit = () => {
        if (!isValid || !componentVariantId)
            return;
        persistDeliveries(form.deliveries, { closeOnSuccess: true }).catch(() => { });
    };
    return (React.createElement(Modal, { alwaysScroll: true, name: "deliveryPlanner", size: "xs", header: React.createElement(Label, { weight: "strong" },
            React.createElement(PlanSvg, null),
            " ",
            t('delivery')) },
        React.createElement(Grid, { columns: 1, gap: 24 },
            React.createElement(Grid, { columns: 1, gap: 12 },
                React.createElement(ParameterDetails, { size: "l", label: t('component'), value: componentName }),
                React.createElement("div", { style: {
                        border: '1px solid var(--static-stroke-subtle)',
                        borderRadius: '6px',
                        padding: '8px 6px',
                    } },
                    React.createElement(Grid, { columns: 2, gap: 0 },
                        React.createElement("div", { style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                gap: '4px',
                            } },
                            React.createElement(BoxIcon, null),
                            React.createElement("span", null, t('ordered')),
                            React.createElement("span", null, formatQuantity(form.ordered))),
                        React.createElement("div", { style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-start',
                                padding: '0 0 0 8px',
                                gap: '4px',
                            } },
                            React.createElement("span", null, t('delivered')),
                            React.createElement("span", null, formatQuantity(totalCollected)))))),
            productionEndDate && (React.createElement("div", { style: {
                    border: '1px solid var(--static-stroke-subtle)',
                    borderRadius: '6px',
                    padding: '12px',
                    backgroundColor: 'var(--static-fill-alpha-base)',
                } },
                React.createElement("div", { style: { fontSize: '12px', color: 'var(--static-text-subtle)', marginBottom: '4px' } }, t('first_delivery_date')),
                React.createElement("div", { style: { fontSize: '14px', fontWeight: 500 } }, productionEndDate.format('DD.MM.YYYY')),
                form.deliveries.length > 0 &&
                    (() => {
                        const plannedWithDate = form.deliveries.filter((d) => (d.type === 'PLANNED' || d.type === 'PENDING_APPROVAL') && d.date !== null);
                        if (plannedWithDate.length === 0) {
                            return null;
                        }
                        const latestPlannedDate = plannedWithDate
                            .map((d) => d.date)
                            .sort((a, b) => b.valueOf() - a.valueOf())[0];
                        // Calculate delay if latest planned date is after production end date
                        if (latestPlannedDate &&
                            latestPlannedDate.isAfter(productionEndDate, 'day')) {
                            const delayDays = latestPlannedDate.diff(productionEndDate, 'days');
                            return (React.createElement("div", { style: {
                                    fontSize: '12px',
                                    color: 'var(--static-text-subtle)',
                                    marginTop: '4px',
                                } },
                                React.createElement("span", { style: { color: '#e74c3c' } },
                                    t('delayed_by'),
                                    " ",
                                    delayDays,
                                    " ",
                                    t('days'))));
                        }
                        return null;
                    })())),
            React.createElement(Grid, { columns: 1, gap: 12 },
                React.createElement(InputLabel, { text: t('planned_delivery') }),
                sortedDeliveries.map((item) => {
                    const statusDisplay = getDeliveryStatusDisplay(item);
                    return (React.createElement("div", { key: item.id, style: {
                            display: 'grid',
                            gridTemplateColumns: '32px 60px 110px 30px 32px',
                            gap: '6px',
                            alignItems: 'center',
                        } },
                        form.deliveries.length > 1 ? (React.createElement(Button, { shape: "squared", theme: "border", onClick: () => handleDeleteDelivery(item.id) }, "\u2212")) : (React.createElement("div", null)),
                        React.createElement(InputNumber, { name: item.id, value: item.quantity, onChange: handleInputChange, decimal: 2, noMaxWidth: true }),
                        React.createElement(Dropdown, { closeOnClick: false, shape: "block", trigger: React.createElement(Button, { shape: "block", theme: "border", leftIcon: React.createElement(CalendarIcon, null) }, item.date ? `${item.date.format('DD.MM.YYYY')}` : '') }, (close) => (React.createElement(CalendarRange, { minDate: moment(orderDaetials.createdDate).add(1, 'day'), startDate: item.date, endDate: item.date, onDateChange: handleDateChange(item.id, close), singleDate: true }))),
                        React.createElement(Tooltip, { theme: "inverse", content: statusDisplay.label, position: "top", delay: 200 },
                            React.createElement("div", { style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                    width: '30px',
                                    height: '22px',
                                    background: statusDisplay.color,
                                } }, statusDisplay.icon)),
                        React.createElement("div", { style: {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '32px',
                            } },
                            React.createElement(Tooltip, { theme: "inverse", content: t('delivered'), position: "top", delay: 200 },
                                React.createElement(Switch, { value: item.type === 'COLLECTED', onChange: () => handleToggleDeliveryState(item) })))));
                }),
                React.createElement(Button, { theme: "border", onClick: addNewDelivery },
                    "+ ",
                    t('part_delivery'))),
            errorMessage && React.createElement(InfoBox, { type: "alert", text: errorMessage, noBorder: true }),
            infoMessage && React.createElement(InfoBox, { type: "warning", text: infoMessage, noBorder: true }),
            React.createElement(Grid, { columns: 2, gap: 12 },
                React.createElement(Button, { theme: "border", onClick: handleClearAll, disabled: saving }, t('clear_all')),
                React.createElement(Button, { color: "primary", onClick: handleSubmit, disabled: !isValid, loading: saving }, t('plan'))))));
}
export default memo(DeliveryPlanner);
